/**
 * How a Document KIND looks — one icon and one type badge per kind, read by
 * every Library surface that shows a document row (the Documents list and the
 * Favorites page).
 *
 * Decision 19 (LB3-T5): a wiki article is a document. What a row IS is its
 * Document kind (`DocumentKindSchema` in @argusai/shared: file | article |
 * synced), taken from the API — never inferred from which list it came from
 * or from a free-text document type. Before this file each page kept its own
 * private switch over its own vocabulary ('uploaded' / 'external' on one,
 * 'wiki' / 'external' on the other), so the two could disagree about the same
 * document.
 *
 * Presentation only: the colours and labels are exactly the ones both pages
 * already rendered (no layout or visual change). The icon's SIZE stays the
 * caller's (the list draws 14px icons, the Favorites table 16px).
 */
import { BookOpen, Cloud, FileText, type LucideIcon } from 'lucide-react';
import { DocumentKindSchema, type ArticleState, type DocumentKind } from '@argusai/shared';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface KindPresentation {
  /** The type badge's text. */
  label: string;
  icon: LucideIcon;
  /** The icon's colour classes (light + dark). */
  iconColor: string;
  /** The type badge's colour classes. */
  badgeColor: string;
}

/**
 * One entry per Document kind. A `Record<DocumentKind, …>` — adding a kind to
 * the shared schema without saying how it looks fails the typecheck.
 */
export const DOCUMENT_KIND_PRESENTATION: Readonly<Record<DocumentKind, KindPresentation>> = Object.freeze({
  article: {
    label: 'Article',
    icon: BookOpen,
    iconColor: 'text-purple-600 dark:text-purple-400',
    badgeColor: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
  },
  file: {
    label: 'File',
    icon: FileText,
    iconColor: 'text-blue-600 dark:text-blue-400',
    badgeColor: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  },
  synced: {
    label: 'Synced',
    icon: Cloud,
    iconColor: 'text-sky-600 dark:text-sky-400',
    badgeColor: 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20',
  },
});

/**
 * The Type filter's label for each kind (plural — "Articles", "Uploaded",
 * "Synced"). A Record over DocumentKindSchema, so the filter's options are
 * the schema's kinds, never a list written out in a page (L6-19).
 */
export const DOCUMENT_KIND_FILTER_LABEL: Readonly<Record<DocumentKind, string>> = Object.freeze({
  file: 'Uploaded',
  article: 'Articles',
  synced: 'Synced',
});

/**
 * How a row whose kind is missing or outside the alphabet looks (LB3T5-BF-05):
 * a neutral document, never a guessed kind. A row like that comes only from
 * an API that predates `kind` (a web bundle deployed ahead of its API, or a
 * cached response); without this the lookup below threw while rendering and
 * took the whole list down to the error boundary instead of one row.
 */
const UNKNOWN_KIND_PRESENTATION: KindPresentation = Object.freeze({
  label: 'Document',
  icon: FileText,
  iconColor: 'text-muted-foreground',
  badgeColor: '',
});

/** The presentation of `kind`, or the neutral one for a kind this build does not know. */
export function documentKindPresentation(kind: unknown): { kind: DocumentKind | 'unknown'; presentation: KindPresentation } {
  const parsed = DocumentKindSchema.safeParse(kind);
  return parsed.success
    ? { kind: parsed.data, presentation: DOCUMENT_KIND_PRESENTATION[parsed.data] }
    : { kind: 'unknown', presentation: UNKNOWN_KIND_PRESENTATION };
}

/** The kind's icon. `className` carries the size (and any layout class). */
export function DocumentKindIcon({ kind, className }: { kind: DocumentKind | null | undefined; className?: string }) {
  const { kind: known, presentation } = documentKindPresentation(kind);
  const { icon: Icon, iconColor } = presentation;
  return <Icon className={cn(className, iconColor)} data-kind={known} />;
}

/** The kind's type badge ("Article" / "File" / "Synced"). */
export function DocumentKindBadge({ kind }: { kind: DocumentKind | null | undefined }) {
  const { label, badgeColor } = documentKindPresentation(kind).presentation;
  return (
    <Badge variant="outline" className={cn('text-[9px]', badgeColor)}>
      {label}
    </Badge>
  );
}

/**
 * How each article state is labelled — a Record over ArticleStateSchema, so a
 * new state without a label fails the typecheck. The tenant list's Status
 * filter (an article's state, LB6T2-BUG-02) and the System Library's lifecycle
 * filter both read it.
 */
export const ARTICLE_STATE_LABEL: Readonly<Record<ArticleState, string>> = Object.freeze({
  draft: 'Draft',
  published: 'Published',
  deprecated: 'Deprecated',
  retired: 'Retired',
});
